import { Button, Checkbox, DatePicker, TextField } from '@shared/ui/kit';
import { FC } from 'react';
import stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { ChevronLeftIcon, ChevronDownIcon, CalendarIcon } from '@shared/assets/icons';
import { InvitationType, StepInfo } from '@features/modal-invitation/model/types';
import { TicketDeliveryMethod } from '@pages/event/api/useSendMessages';
import { EventSettings } from '@features/api/use-get-settings';
import { formatDate } from '@shared/libs/format-date';
import { LockIcon } from '@shared/assets/icons/lock';
import { CustomDropdown } from '@shared/ui/dropdown';
import { Tiptap } from '@shared/ui/tiptap';
import { Alert, ALERT_VARIANTS } from '@features/modal-invitation/ui/alert';
import { StylesText } from '@entities/leadCollector';
import { UnstyledButton } from '@shared/ui/unstyled-button';

// TODO: старый флоу отправки приглашений - этот шаг и связанный код снесется уже переходим на новый флоу
// Пропсы не группируем так как компонент будет сносится.

interface StepTicketProps {
  ticketDeliveryMethod: TicketDeliveryMethod | null;
  ticketDeliveryMethodError: string | false;
  ticketMessage: string;
  ticketMessageError: string | false;
  ticketMessageParts: number;
  handleTicketDeliveryChange: (value: InvitationType[]) => void;
  handleTicketMessageChange: (event: string) => void;
  handleNext: () => void;
  stepInfo: StepInfo;
  invitationTypes: InvitationType[];
  maxMessageLength?: number;
  handleBack: () => void;
  sendTicketAfterAcceptAt: Date | null;
  onTicketDateChange: (date: Date | null) => void;
  eventSettings: EventSettings | null;
  invitationExpiresAt: Date | null;
  eventDateEnd?: string;
  sendTicketDateError: string | false;
  isTicketDateTouched: boolean;
  onOpenSettings?: () => void;
  handleClose?: () => void;
  totalInvites?: number;
  isSkipTicketSendChecked: boolean;
  onSkipTicketSendChange: (isChecked: boolean) => void;
}

export const StepTicket: FC<StepTicketProps> = ({
  ticketDeliveryMethod,
  // ticketDeliveryMethodError,
  ticketMessage,
  ticketMessageError,
  ticketMessageParts,
  handleTicketDeliveryChange,
  handleTicketMessageChange,
  handleNext,
  stepInfo,
  invitationTypes,
  handleBack,
  sendTicketAfterAcceptAt,
  onTicketDateChange,
  invitationExpiresAt,
  eventDateEnd,
  eventSettings,
  sendTicketDateError,
  isTicketDateTouched,
  onOpenSettings,
  handleClose,
  totalInvites = 0,
  isSkipTicketSendChecked,
  onSkipTicketSendChange,
}) => {
  const isRepeatSending = totalInvites > 0;

  const isSmsMode = ticketDeliveryMethod === TicketDeliveryMethod.SMS;
  const isBothMode = ticketDeliveryMethod === TicketDeliveryMethod.Both;
  const isTicketSettingsLocked = isRepeatSending || isSkipTicketSendChecked;

  const selectedSendType = invitationTypes.find(({ value }) => value === ticketDeliveryMethod) || undefined;

  // Проверка изменения даты отправки билета
  const hasTicketDateChanged = () => {
    if (!eventSettings?.sendTicketAfterAcceptAt || !sendTicketAfterAcceptAt) return false;

    const previousDate = new Date(eventSettings.sendTicketAfterAcceptAt);
    previousDate.setHours(0, 0, 0, 0);

    const currentDate = new Date(sendTicketAfterAcceptAt);
    currentDate.setHours(0, 0, 0, 0);

    return previousDate.getTime() !== currentDate.getTime();
  };

  // Проверка на дату в прошлом
  const isTicketDateInPast = () => {
    if (!sendTicketAfterAcceptAt) return false;

    const ticketDate = new Date(sendTicketAfterAcceptAt);
    ticketDate.setHours(0, 0, 0, 0);

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    return ticketDate < currentDate;
  };

  const isTicketDateBeforeInvitationExpiry = () => {
    if (!sendTicketAfterAcceptAt || !invitationExpiresAt) return false;

    const ticketDate = new Date(sendTicketAfterAcceptAt);
    ticketDate.setHours(0, 0, 0, 0);

    const invitationDate = new Date(invitationExpiresAt);
    invitationDate.setHours(0, 0, 0, 0);

    return ticketDate < invitationDate;
  };

  const isTicketDateAfterEventEnd = () => {
    if (!sendTicketAfterAcceptAt || !eventDateEnd) return false;

    const ticketDate = new Date(sendTicketAfterAcceptAt);
    ticketDate.setHours(0, 0, 0, 0);

    const eventEndDate = new Date(eventDateEnd);
    eventEndDate.setHours(0, 0, 0, 0);

    return ticketDate > eventEndDate;
  };

  const shouldShowPastDateWarning = isRepeatSending && isTicketDateInPast();
  const shouldShowDateWarning =
    isTicketDateBeforeInvitationExpiry() && !shouldShowPastDateWarning && !sendTicketDateError;
  const shouldShowEventEndWarning = isTicketDateAfterEventEnd();
  const shouldShowTicketDateChangeInfo =
    isRepeatSending &&
    hasTicketDateChanged() &&
    !sendTicketDateError &&
    !shouldShowDateWarning &&
    !shouldShowEventEndWarning &&
    !shouldShowPastDateWarning;

  const handleOpenSettings = () => {
    handleClose?.();
    onOpenSettings?.();
  };
  const handleSkipTicketSendChange = () => {
    if (isRepeatSending) return;

    if (isSkipTicketSendChecked) {
      onSkipTicketSendChange(false);
      return;
    }

    onTicketDateChange(null);
    onSkipTicketSendChange(true);
  };

  const hasAnyWarning = shouldShowDateWarning || shouldShowEventEndWarning || shouldShowPastDateWarning;
  const isNextButtonDisabled = isSkipTicketSendChecked
    ? false
    : !ticketMessage.trim() || !ticketDeliveryMethod || hasAnyWarning || !!sendTicketDateError;
  const shouldShowNoTicketDateInfoAlert =
    !isSkipTicketSendChecked &&
    !sendTicketAfterAcceptAt &&
    !shouldShowDateWarning &&
    !shouldShowEventEndWarning &&
    !isRepeatSending;

  const getTextarea = () => {
    if (isSmsMode) {
      return (
        <>
          <p {...stylex.props([styles.textCountBase, styles.textCountSms])}>
            {ticketMessage.length} ({ticketMessageParts} смс)
            {isTicketSettingsLocked && (
              <span {...stylex.props(styles.lockIcon)}>
                <LockIcon />
              </span>
            )}
          </p>

          <div {...stylex.props(styles.textFieldContainer)}>
            <div {...stylex.props(styles.labelPreviewText)}>Текст билета *</div>
            {ticketMessageError && <div {...stylex.props(styles.textareaErrorText)}>{ticketMessageError}</div>}
            <TextField
              labelMsg=""
              changeFunction={(event) => handleTicketMessageChange(event.target.value.replace(/\r?\n/g, ''))}
              placeholder="Ввести текст"
              value={ticketMessage}
              settings={{
                width: '432px',
                resize: 'none',
                cursor: 'text',
                height: '78px',
                minHeight: '64px',
                overflow: 'auto',
              }}
              status={ticketMessageError ? 'error' : 'default'}
              textareaMessage={ticketMessageError || undefined}
              isDisabled={isTicketSettingsLocked}
            />
          </div>
        </>
      );
    }

    return (
      <>
        {isTicketSettingsLocked && (
          <span {...stylex.props(styles.lockIcon, styles.tiltapLockIcon)}>
            <LockIcon />
          </span>
        )}
        <div {...stylex.props(styles.message, isTicketSettingsLocked && styles.messageDisabled)}>
          <Tiptap
            {...stylex.props(styles.tiptap, isTicketSettingsLocked && styles.tiptapDisabled)}
            placeholder="Текст билета"
            onChange={(html) => handleTicketMessageChange(html)}
            properties={{ link: true, styles: [StylesText.BOLD, StylesText.ITALIC, StylesText.UNDERLINE] }}
            withToolbar={!isTicketSettingsLocked}
            editable={!isTicketSettingsLocked}
            hiddenStyles={[StylesText.LEFT, StylesText.CENTER, StylesText.RIGHT, StylesText.JUSTIFY]}
            content={ticketMessage}
          />
        </div>

        {!isSkipTicketSendChecked && isBothMode && (
          <div {...stylex.props(styles.alertTopSpacing)}>
            <Alert variant={ALERT_VARIANTS.INFO} maxWidth="432px">
              Форматирование будет применено только к email
            </Alert>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <div {...stylex.props(styles.inputWrapper)}>
        <div {...stylex.props(styles.dropdownContainer)}>
          <div {...stylex.props(isTicketSettingsLocked ? styles.dateWrapperLocked : styles.dateWrapper)}>
            <div {...stylex.props(styles.labelContainer)}>
              <p {...stylex.props(isTicketSettingsLocked ? styles.labelTypeLocked : styles.labelType)}>
                Дата отправки билета
              </p>
              {isTicketSettingsLocked && (
                <div {...stylex.props(styles.labelIcon)}>
                  <LockIcon />
                </div>
              )}
            </div>
            {isTicketSettingsLocked ? (
              <div {...stylex.props(styles.lockedDateContainer)}>
                <div {...stylex.props(styles.lockedDateText)}>
                  {sendTicketAfterAcceptAt ? formatDate(sendTicketAfterAcceptAt.toISOString()) : '-'}
                  <div {...stylex.props(styles.calendarIcon)}>
                    <CalendarIcon />
                  </div>
                </div>
              </div>
            ) : (
              <DatePicker
                labelText=""
                size="small"
                isRange={false}
                selectedDate={sendTicketAfterAcceptAt}
                setNewSelectedDate={(dates) => onTicketDateChange(dates[0] || null)}
              />
            )}
            {sendTicketDateError && isTicketDateTouched && (
              <div {...stylex.props(styles.errorText)}>{sendTicketDateError}</div>
            )}
          </div>
          {isTicketSettingsLocked ? (
            <div>
              <div {...stylex.props(styles.labelContainer)}>
                <p {...stylex.props(styles.labelTypeLocked)}>Тип отправки билета *</p>

                <div {...stylex.props(styles.labelIcon)}>
                  <LockIcon />
                </div>
              </div>

              <div {...stylex.props(styles.lockedDropdownContainer)}>
                <div {...stylex.props(styles.lockedDropdownText)}>
                  {invitationTypes.find((opt) => opt.value === ticketDeliveryMethod)?.label || '-'}

                  <div {...stylex.props(styles.dropdownIcon)}>
                    <ChevronDownIcon />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <CustomDropdown
              labelMessage="Тип отправки билета *"
              placeholder="Выбрать тип"
              options={invitationTypes}
              itemText="label"
              itemValue="value"
              value={selectedSendType}
              isRadio={false}
              fullWidth
              changeFunc={(option) => {
                option && handleTicketDeliveryChange([option]);
              }}
            />
          )}
        </div>

        <div
          {...stylex.props(styles.skipTicketSendContainer, isRepeatSending && styles.skipTicketSendContainerReadonly)}
        >
          <Checkbox isChecked={isSkipTicketSendChecked} onChange={handleSkipTicketSendChange} />
          <p {...stylex.props(styles.skipTicketSendText)}>Не отправлять билет</p>
        </div>

        {getTextarea()}

        <div {...stylex.props(styles.previewContainer)}>
          <div {...stylex.props(styles.labelPreview)}>Предварительный просмотр билета</div>
          <div {...stylex.props(styles.areaPreview)}>
            {isSmsMode ? (
              <div {...stylex.props(styles.messagePreview)}>{ticketMessage}</div>
            ) : (
              <div {...stylex.props(styles.tiptap, styles.previewText)}>
                <div dangerouslySetInnerHTML={{ __html: ticketMessage }} />
              </div>
            )}
            <div {...stylex.props(styles.linkPreview)}>{globalThis.window.location.href}</div>
          </div>
        </div>

        {shouldShowNoTicketDateInfoAlert && (
          <div {...stylex.props(styles.alertTopSpacing)}>
            <Alert variant={ALERT_VARIANTS.INFO} maxWidth="432px">
              Вы не выбрали дату отправки билета. Билеты будут отправлены сразу после подтверждения приглашения
            </Alert>
          </div>
        )}

        {shouldShowDateWarning && (
          <div {...stylex.props(styles.alertTopSpacing)}>
            <Alert variant={ALERT_VARIANTS.ERROR} maxWidth="432px">
              Дата отправки билета раньше, чем срок действия приглашения{' '}
              {formatDate(invitationExpiresAt!.toISOString())}.
            </Alert>
          </div>
        )}

        {shouldShowEventEndWarning && (
          <div {...stylex.props(styles.alertTopSpacing)}>
            <Alert variant={ALERT_VARIANTS.ERROR} maxWidth="432px">
              Дата отправки билета позже даты проведения мероприятия {eventDateEnd && formatDate(eventDateEnd)}.
              Перенесите отправку билетов
            </Alert>
          </div>
        )}

        {shouldShowTicketDateChangeInfo && eventSettings?.sendTicketAfterAcceptAt && sendTicketAfterAcceptAt && (
          <div {...stylex.props(styles.alertTopSpacing)}>
            <Alert variant={ALERT_VARIANTS.EXCLAMATION} maxWidth="432px">
              <h3 {...stylex.props(styles.exclamationTitle)}>Изменилась дата отправки билета</h3>
              <p>Предыдущая дата отправки билета: {formatDate(eventSettings.sendTicketAfterAcceptAt)}</p>
              <p>Новая дата отправки билета: {formatDate(sendTicketAfterAcceptAt.toISOString())}</p>
            </Alert>
          </div>
        )}

        {shouldShowPastDateWarning && (
          <div {...stylex.props(styles.alertTopSpacing)}>
            <Alert variant={ALERT_VARIANTS.ERROR} maxWidth="432px">
              Дата отправки билета уже прошла.
            </Alert>
          </div>
        )}

        {isSkipTicketSendChecked && (
          <div {...stylex.props(styles.alertTopSpacing)}>
            <Alert variant={ALERT_VARIANTS.INFO} maxWidth="432px">
              При выборе опции «Не отправлять билет» участники, уже принявшие приглашение, не получат его
            </Alert>
          </div>
        )}

        {isRepeatSending && (
          <div {...stylex.props(styles.alertTopSpacing)}>
            <Alert variant={ALERT_VARIANTS.INFO} maxWidth="432px">
              Настройки билета заданы в настройках мероприятия.{' '}
              <UnstyledButton {...stylex.props(styles.infoLink)} onClick={handleOpenSettings}>
                Перейти в настройки для изменения
              </UnstyledButton>
            </Alert>
          </div>
        )}
      </div>
      <div {...stylex.props(styles.actions)}>
        <div {...stylex.props(styles.stepsContainer)}>
          <Button size="small" variant="secondary" onClick={handleBack}>
            <ChevronLeftIcon />
          </Button>
          <div {...stylex.props(styles.stepInfo)}>
            <div {...stylex.props(styles.step)}>
              Шаг {stepInfo.current} из {stepInfo.total}
            </div>
            <div {...stylex.props(styles.info)}>
              Следующий: <span {...stylex.props(styles.nextStep)}>{stepInfo.next}</span>
            </div>
          </div>
        </div>

        <Button
          onClick={handleNext}
          isDisabled={isNextButtonDisabled}
          variant="accent"
          settings={{ padding: '8px 20px' }}
          size="small"
        >
          Далее
        </Button>
      </div>
    </>
  );
};
